import * as React from 'react';

import type { UserAPITokenExtended } from '@gitbook/api-client';
import { APITOKEN_LABEL_LIMIT } from '@gitbook/app-database';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldGroup,
    Heading,
    Label,
    LoadingIndicator,
    TextInput,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient } from '../../state';

/**
 * Dialog to create a new API token.
 */
export function CreateAPITokenDialog() {
    return (
        <Dialog>
            <CreateAPITokenDialogContent />
        </Dialog>
    );
}

function CreateAPITokenDialogContent(): React.ReactElement {
    const [loading, wrapLoading] = useLoadingState();

    const [newToken, setNewToken] = React.useState<null | UserAPITokenExtended>(null);
    const [tokenLabel, setTokenLabel] = React.useState('');
    const [isLabelValid, setIsLabelValid] = React.useState(false);
    const [validationMessage, setValidationMessage] = React.useState<string | undefined>(undefined);

    const onConfirm = async () => {
        await wrapLoading(async () => {
            if (tokenLabel) {
                const { data: newToken } = await apiClient.users.createApiToken({
                    label: tokenLabel,
                });

                setNewToken(newToken);

                if (newToken.token) {
                    setNewToken(newToken);
                    return;
                }
            }
        });
    };

    const onChangeLabel = (value: string) => {
        setTokenLabel(value);
        validateLabel(value);
    };

    const validateLabel = (label: string) => {
        const tooLong = label.length >= APITOKEN_LABEL_LIMIT;
        const tooShort = label.length === 0;

        const isValid = !tooLong && !tooShort;

        setIsLabelValid(isValid);

        if (tooLong) {
            setValidationMessage(
                `Label is too long, it should be less than ${APITOKEN_LABEL_LIMIT} characters`
            );
        } else {
            setValidationMessage(undefined);
        }
    };

    if (newToken) {
        return (
            <>
                <DialogHeader>
                    <Heading>API token created</Heading>
                    <Description>
                        This token will grant access to the GitBook API with all the same
                        permissions of your current user account.
                    </Description>
                </DialogHeader>
                <DialogBody>
                    <TextInput value={newToken.token} readOnly copyable />
                    <p className="mt-4">
                        Be sure to copy the token below as it will not be visible again once you
                        close this dialog.
                    </p>
                </DialogBody>
                <DialogFooter>
                    <DialogCloseButton />
                    <Button
                        onPress={() => clipboard.writeText(newToken.token)}
                        kind="primary"
                        label="Copy"
                        leadingIcon={Icon.Copy}
                    />
                </DialogFooter>
            </>
        );
    }

    return (
        <>
            <DialogHeader>
                <Heading>Create an API token</Heading>
            </DialogHeader>
            <DialogBody>
                <FieldGroup>
                    <Label>Label</Label>
                    <Description>What's this token for?</Description>
                    <TextInput
                        value={tokenLabel}
                        onChange={(event) => onChangeLabel(event.target.value)}
                        autoFocus
                        feedback={
                            validationMessage
                                ? { type: 'danger', message: validationMessage }
                                : undefined
                        }
                        onSubmit={() => {
                            if (isLabelValid && !loading) {
                                onConfirm();
                            }
                        }}
                    />
                </FieldGroup>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    onPress={onConfirm}
                    kind="primary"
                    disabled={loading || !isLabelValid}
                    label="Create"
                    leadingIcon={loading ? LoadingIndicator : undefined}
                />
            </DialogFooter>
        </>
    );
}
